package com.dotdrives.model

/**
 * The Reports (Summaries) desktop frame (`design/portal-updates.dc.html`) is computed
 * directly from `donations` joined on `targets` and `donations.steward_user_id`
 * (docs/schema.md: "Updates and reports" -- "nothing is stored for them"). `donations`
 * RLS is `has_perm_strict('donation.read')`, which admits `owner|admin|member` only --
 * `field` is excluded, same as every other table in the Donations section. This whole
 * model tree is therefore staff-only data; [GivingSummaryController] never issues the
 * query that would produce one for a field-role viewer in the first place.
 */
data class TargetGivingSummary(
    val targetId: String,
    val name: String,
    val raisedAmount: Double,
    val goalAmount: Double?,
    val goalStart: kotlinx.datetime.LocalDate? = null,
    val goalEnd: kotlinx.datetime.LocalDate? = null,
)

data class OrgBudgetSummary(
    val raisedAmount: Double,
    val goalAmount: Double?,
    val goalStart: kotlinx.datetime.LocalDate? = null,
    val goalEnd: kotlinx.datetime.LocalDate? = null,
)

data class StewardGivingSummary(
    val userId: String,
    val name: String,
    val raisedAmount: Double,
    /** `budgets` where `scope='steward'` and `scope_ref = userId`, for the period overlapping
     *  the selected year (docs/schema.md: "budgets ... for pacing and leaderboards"). Null when
     *  no budget row exists for this steward -- the leaderboard renders "--" for attainment
     *  rather than dividing by zero or falling back to a share-of-total, which the design's own
     *  numbers rule out (SJ $84.2K/70%, MR $71.5K/65%, DL $52.8K/88% -- the lowest earner has
     *  the highest percentage, so the figure is per-steward goal attainment, not share of the
     *  leaderboard total). */
    val budgetAmount: Double? = null,
    val budgetStart: kotlinx.datetime.LocalDate? = null,
    val budgetEnd: kotlinx.datetime.LocalDate? = null,
)

/**
 * One calendar month. `onlineAmount` includes `recurringAmount` as a subset (a
 * subscription-sourced gift is always online) -- `totalAmount` is `onlineAmount +
 * manualAmount`, not all three summed, matching the design's own numbers (August:
 * online $14.4K + manual $17.4K = total $31.8K, with the $3.1K recurring figure already
 * inside the $14.4K).
 */
data class MonthlyGivingSummary(
    val year: Int,
    val month: Int,
    val label: String,
    val giftCount: Int,
    val onlineAmount: Double,
    val manualAmount: Double,
    val recurringAmount: Double,
) {
    val totalAmount: Double get() = onlineAmount + manualAmount
}

data class GivingSummary(
    val targets: List<TargetGivingSummary>,
    val orgBudget: OrgBudgetSummary,
    val stewards: List<StewardGivingSummary>,
    val months: List<MonthlyGivingSummary>,
    val period: ReportPeriod? = null,
    val priorRaisedAmount: Double = 0.0,
    val giftCount: Int = months.sumOf { it.giftCount },
    val recurringAmount: Double = months.sumOf { it.recurringAmount },
    val priorMonths: List<MonthlyGivingSummary> = emptyList(),
    val targetCatalog: List<TargetGivingSummary> = targets,
    val stewardCatalog: List<StewardGivingSummary> = stewards,
    val asOf: kotlinx.datetime.LocalDate? = period?.endInclusive,
    val hasAnyGifts: Boolean = giftCount > 0,
)
